import React from 'react';
import { useRouter } from 'next/compat/router';
import { observer } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';

import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import usePriceRangeBands from 'hooks/use-price-range-bands';
import useGlobalCategoryFilterOptions from 'hooks/use-global-category-filter-options';
import useUI from 'hooks/use-ui';

import Sidebar from './sidebar';
import { useCustomTagFilterForCategory } from './custom-tag-filter/use-custom-tag-filter-for-category';

function MenuSidebar({ specialId, specialType, collectionId, customCategory }) {
  const category = useDispensaryCategory();
  const { dispensary } = useDispensary();
  const { route } = useRouter() ?? {};
  const { onSearchPage, onCollectionsPage } = useUI();
  const flags = useFlags();

  const {
    apolloValues: { loading },
    ...menuFilterOptions
  } = useMenuFilterOptions({ specialId, specialType, collectionId });

  const onBrandsPage = _.includes(route, 'brands');

  const { tags: customTagNames, title: customTagTitle } = useCustomTagFilterForCategory({
    category,
    onSearchPage,
    onCollectionsPage,
    onBrandsPage,
    customCategory,
  });

  // Independent of the menuFiltersV2 facet query above (own request/cache), so a slow
  // or failed price-bands fetch never blocks the rest of the filter sidebar from rendering.
  const { bands: priceBands } = usePriceRangeBands();
  menuFilterOptions.priceBands = priceBands;

  // Only fetch the unscoped global category list when it could actually be rendered:
  // the LD flag is on, we're on a category page (not search/collections/brands), and
  // it's not a custom category (each has its own gated path). See PF-9862.
  const globalCategoriesSidebarEnabled = flags['ecomm.global-categories-sidebar.rollout'] ?? false;
  const shouldFetchGlobalCategories =
    globalCategoriesSidebarEnabled &&
    Boolean(category?.key) &&
    !onSearchPage &&
    !onCollectionsPage &&
    !onBrandsPage &&
    !customCategory;

  const { globalCategoriesSubcategories } = useGlobalCategoryFilterOptions({ skip: !shouldFetchGlobalCategories });

  const noFilters = _.every(menuFilterOptions, (opt) => _.isEmpty(opt)) && _.isEmpty(customTagNames);
  if (noFilters && !loading) {
    return null;
  }

  return (
    <Sidebar
      menuFilterOptions={menuFilterOptions}
      category={category}
      isLoading={loading}
      route={route}
      dispensary={dispensary}
      onSearchPage={onSearchPage}
      onCollectionsPage={onCollectionsPage}
      customCategory={customCategory}
      customTagNames={customTagNames}
      customTagTitle={customTagTitle}
      globalCategoriesSubcategories={globalCategoriesSubcategories}
    />
  );
}

export default observer(MenuSidebar);
